import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// PayPal's JS buttons cannot run inside the Android WebView, so USD checkout is
// handed to the system browser and captured when the user returns to the app.
export function useNativePayPal(onPaid) {
  const [pendingOrder, setPendingOrder] = useState("");
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  const paidRef = useRef(false);
  const paidCb = useRef(onPaid);
  paidCb.current = onPaid;

  useEffect(() => {
    if (!pendingOrder) return;
    let alive = true;
    const check = async () => {
      if (paidRef.current) return;
      try {
        const r = await axios.post(`${API}/payments/paypal/capture/${pendingOrder}`, {}, { withCredentials: true });
        if (alive && (r.data.status === "success" || r.data.status === "COMPLETED")) {
          paidRef.current = true;
          setPendingOrder("");
          await paidCb.current?.(r.data);
        }
      } catch (_) { /* not approved yet */ }
    };
    const timer = setInterval(check, 5000);
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    return () => {
      alive = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", check);
    };
  }, [pendingOrder]);

  const start = async (plan, payer = {}) => {
    setError(""); setStarting(true);
    try {
      const res = await axios.post(`${API}/payments/paypal/create-order`, {
        plan, name: payer.name || "", email: payer.email || "", phone: payer.phone || "",
      }, { withCredentials: true });
      const { approve_url, paypal_order_id } = res.data;
      if (!approve_url) throw new Error("no approve url");
      if (window.AstroFateNative?.openExternal) window.AstroFateNative.openExternal(approve_url);
      else window.open(approve_url, "_blank", "noopener");
      setPendingOrder(paypal_order_id);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not start the PayPal checkout. Please try again.");
    } finally {
      setStarting(false);
    }
  };

  return { start, pendingOrder, error, starting };
}
